import { useEffect } from "react";

function normalizeKey(event: KeyboardEvent) {
  const keys: string[] = [];
  if (event.ctrlKey) keys.push("Control");
  if (event.shiftKey) keys.push("Shift");
  if (event.altKey) keys.push("Alt");
  keys.push(event.key);
  return keys.join("+");
}

export type HotkeyHandler<TCtx = {}> =
  (ctx?: TCtx & { event: KeyboardEvent; key: string }) => void | boolean;

export type KeysMap<TCtx = {}> = Record<string, HotkeyHandler<TCtx>>;

function shouldIgnoreHotkeyTarget(event: KeyboardEvent) {
  const target = event.target as HTMLElement | null;
  return (
    target &&
    (
      target.tagName === "INPUT" ||
      target.tagName === "TEXTAREA" ||
      target.isContentEditable
    )
  );
}

function runHotkeyHandler<TCtx = {}>(
  keysMap: KeysMap<TCtx>,
  event: KeyboardEvent,
  ctx?: TCtx,
) {
  if (shouldIgnoreHotkeyTarget(event)) return;

  const key = normalizeKey(event);
  const handler = (keysMap as any)[key] || (keysMap as any)[event.key];

  if (handler) {
    const result = handler({ ...(ctx as object), event, key } as any);
    if (result !== false) {
      event.preventDefault();
    }
  }
}

export function useHotkeys<TCtx = {}>(
  keysMap: KeysMap<TCtx>,
  ctx?: TCtx,
  keyUpKeysMap?: KeysMap<TCtx>,
) {
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      runHotkeyHandler(keysMap, event, ctx);
    };

    const handleKeyUp = (event: KeyboardEvent) => {
      if (!keyUpKeysMap) return;
      runHotkeyHandler(keyUpKeysMap, event, ctx);
    };


    window.addEventListener("keydown", handleKeyDown);
    if (keyUpKeysMap) {
      window.addEventListener("keyup", handleKeyUp);
    }
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      if (keyUpKeysMap) {
        window.removeEventListener("keyup", handleKeyUp);
      }
    };
  }, [keysMap, keyUpKeysMap, ctx]);
}
